/**
 * YardMapEditor (#3286)
 *
 * Upload, replace and delete the yard map picture and place the numbered
 * parking spots on it. Every write names the revision (`id` + `generation`) it
 * was based on; a stale one is shown as a notice, the local draft is dropped
 * and the latest map is reloaded. Nothing is retried or overwritten silently.
 * Writes need equipment.manage; without it the map is read-only.
 */

import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  YardMapCanvas,
  type YardMapMarker,
} from '@/components/parking/YardMapCanvas';
import {
  useParkingCurrentStatus,
  type ParkingCurrentStatus,
  type YardMapSummary,
} from '@/hooks/useParking';
import {
  YARD_MAP_ACCEPTED_TYPES,
  isYardMapStaleError,
  spotMapPosition,
  useDeleteYardMap,
  useSaveYardMapPlacements,
  useUploadYardMapImage,
  useYardMapImage,
  validateYardMapFile,
  type YardMapPlacement,
} from '@/hooks/useYardMap';
import { describeApiError } from '@/lib/describeApiError';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';

interface Position {
  x: number;
  y: number;
}

interface EditorSpot {
  spotId: string;
  identifier: string;
  zoneName: string;
  saved: Position | null;
}

const ERROR_CODE_PREFIX = 'equipment:parking.yardMap.errorCode';
const NO_DRAFT: Readonly<Record<string, Position | null>> = {};

/** Four decimals is far finer than a pixel and keeps the request body tidy. */
const tidy = (value: number): number => Math.round(value * 10_000) / 10_000;

function samePosition(a: Position | null, b: Position | null): boolean {
  if (a === null || b === null) return a === b;
  return a.x === b.x && a.y === b.y;
}

function flattenSpots(status: ParkingCurrentStatus): EditorSpot[] {
  return status.zones.flatMap((zone) =>
    zone.spots.map((spot) => ({
      spotId: spot.spotId,
      identifier: spot.identifier,
      zoneName: zone.zoneName,
      saved: spotMapPosition(spot),
    })),
  );
}

export function YardMapEditor() {
  const { t: tc } = useTranslation('common');
  const { data, isLoading, isError, refetch } = useParkingCurrentStatus();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} aria-label={tc('loading')} />
      </Box>
    );
  }
  // A response without the field comes from a server that predates the yard map.
  if (isError || !data || data.yardMap === undefined) return null;

  return <YardMapEditorPanel status={data} yardMap={data.yardMap} onReload={() => void refetch()} />;
}

function YardMapEditorPanel({
  status,
  yardMap,
  onReload,
}: {
  status: ParkingCurrentStatus;
  yardMap: YardMapSummary | null;
  onReload: () => void;
}) {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManage = hasPermission('equipment.manage');

  const upload = useUploadYardMapImage();
  const remove = useDeleteYardMap();
  const save = useSaveYardMapPlacements();
  const pending = upload.isPending || remove.isPending || save.isPending;

  const image = useYardMapImage(yardMap);
  const revisionKey = yardMap ? `${yardMap.id}:${String(yardMap.generation)}` : 'none';
  const [failedRevision, setFailedRevision] = useState<string | null>(null);
  const imageFailed = failedRevision === revisionKey;

  // The draft belongs to one revision; a new revision (own save, replace, or
  // someone else's change) silently starts from the saved positions again.
  const [draftState, setDraftState] = useState<{
    revision: string;
    positions: Record<string, Position | null>;
  }>({ revision: revisionKey, positions: {} });
  const draft = draftState.revision === revisionKey ? draftState.positions : NO_DRAFT;
  const setDraftPosition = (spotId: string, position: Position | null) => {
    setDraftState((prev) => ({
      revision: revisionKey,
      positions: {
        ...(prev.revision === revisionKey ? prev.positions : {}),
        [spotId]: position,
      },
    }));
  };
  const discardDraft = () => setDraftState({ revision: revisionKey, positions: {} });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  // A confirmation is for the revision the user was looking at when it opened.
  // It keeps that exact map; if the map moved on meanwhile the server answers
  // 409 (stale notice) instead of the confirmation hitting the newer revision.
  const [replaceRequest, setReplaceRequest] = useState<{ file: File; map: YardMapSummary } | null>(null);
  const [deleteRequest, setDeleteRequest] = useState<YardMapSummary | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const spots = flattenSpots(status);
  const positionOf = (spot: EditorSpot): Position | null =>
    spot.spotId in draft ? (draft[spot.spotId] ?? null) : spot.saved;
  const changed: YardMapPlacement[] = spots.flatMap((spot) => {
    if (!(spot.spotId in draft)) return [];
    const next = draft[spot.spotId] ?? null;
    if (samePosition(next, spot.saved)) return [];
    return [{ spotId: spot.spotId, mapX: next?.x ?? null, mapY: next?.y ?? null }];
  });
  const dirty = changed.length > 0;
  const selected = spots.find((spot) => spot.spotId === selectedId) ?? null;
  const selectedPosition = selected ? positionOf(selected) : null;

  const handleWriteError = (error: unknown, fallbackKey: string) => {
    if (isYardMapStaleError(error)) {
      // Someone else changed the map: drop the stale draft and load the latest.
      // Never retried, never merged over the newer map.
      discardDraft();
      setSelectedId(null);
      setStale(true);
      onReload();
      return;
    }
    notify.error(
      tc('error'),
      describeApiError(error, tc, { codePrefix: ERROR_CODE_PREFIX, fallback: t(fallbackKey) }),
    );
  };

  const doUpload = async (file: File, map: YardMapSummary | null) => {
    try {
      await upload.mutateAsync({ file, map });
      setStale(false);
      setSelectedId(null);
      if (map) notify.success(t('parking.yardMap.replaced'), t('parking.yardMap.replacedDesc'));
      else notify.success(t('parking.yardMap.uploaded'), t('parking.yardMap.uploadedDesc'));
    } catch (error) {
      handleWriteError(error, 'parking.yardMap.uploadFailed');
    }
  };

  const handleFileChosen = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canManage || pending) return;
    const problem = validateYardMapFile(file);
    if (problem) {
      notify.error(
        tc('error'),
        t(problem === 'type' ? 'parking.yardMap.fileTypeInvalid' : 'parking.yardMap.fileTooLarge'),
      );
      return;
    }
    if (yardMap) setReplaceRequest({ file, map: yardMap });
    else void doUpload(file, null);
  };

  const handleConfirmReplace = () => {
    const request = replaceRequest;
    setReplaceRequest(null);
    if (!request || !canManage || pending) return;
    void doUpload(request.file, request.map);
  };

  const handleConfirmDelete = async () => {
    const map = deleteRequest;
    setDeleteRequest(null);
    if (!map || !canManage || pending) return;
    try {
      await remove.mutateAsync({ map });
      setStale(false);
      setSelectedId(null);
      notify.success(t('parking.yardMap.deleted'), t('parking.yardMap.deletedDesc'));
    } catch (error) {
      handleWriteError(error, 'parking.yardMap.deleteFailed');
    }
  };

  const handleSave = async () => {
    if (!yardMap || !canManage || !dirty || pending) return;
    try {
      await save.mutateAsync({ map: yardMap, placements: changed });
      notify.success(
        t('parking.yardMap.placementsSaved'),
        t('parking.yardMap.placementsSavedDesc', { count: changed.length }),
      );
    } catch (error) {
      handleWriteError(error, 'parking.yardMap.saveFailed');
    }
  };

  const markers: YardMapMarker[] = spots.flatMap((spot) => {
    const position = positionOf(spot);
    if (!position) return [];
    const isSelected = spot.spotId === selectedId;
    return [
      {
        id: spot.spotId,
        identifier: spot.identifier,
        zoneName: spot.zoneName,
        x: position.x,
        y: position.y,
        tone: 'neutral' as const,
        statusLabel: isSelected ? t('parking.yardMap.statusSelected') : t('parking.yardMap.statusPlaced'),
        ariaLabel: t('parking.yardMap.markerAria', {
          identifier: spot.identifier,
          zone: spot.zoneName,
          x: Math.round(position.x * 100),
          y: Math.round(position.y * 100),
        }),
        selected: isSelected,
      },
    ];
  });

  const mapReady = yardMap !== null && image.status === 'ready' && image.url !== null && !imageFailed;

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">{t('parking.yardMap.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('parking.yardMap.description')}
            </Typography>
          </Box>

          {stale && (
            <Alert
              severity="warning"
              closeText={tc('close')}
              onClose={() => setStale(false)}
              action={
                <Button color="inherit" size="small" onClick={onReload}>
                  {t('parking.yardMap.reload')}
                </Button>
              }
            >
              <Typography fontWeight={600}>{t('parking.yardMap.staleTitle')}</Typography>
              <Typography variant="body2">{t('parking.yardMap.staleNotice')}</Typography>
            </Alert>
          )}

          {canManage && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button
                variant={yardMap ? 'outlined' : 'contained'}
                disabled={pending}
                onClick={() => fileInputRef.current?.click()}
                sx={{ minHeight: 44 }}
              >
                {upload.isPending
                  ? t('parking.yardMap.uploading')
                  : yardMap
                    ? t('parking.yardMap.replaceImage')
                    : t('parking.yardMap.uploadImage')}
              </Button>
              {yardMap && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={pending}
                  onClick={() => setDeleteRequest(yardMap)}
                  sx={{ minHeight: 44 }}
                >
                  {t('parking.yardMap.deleteImage')}
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                {t('parking.yardMap.fileHint')}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept={YARD_MAP_ACCEPTED_TYPES.join(',')}
                onChange={handleFileChosen}
                style={{ display: 'none' }}
                tabIndex={-1}
                aria-hidden="true"
              />
            </Stack>
          )}

          {yardMap === null && (
            <Alert severity="info">
              <Typography fontWeight={600}>{t('parking.yardMap.noMap')}</Typography>
              <Typography variant="body2">
                {canManage ? t('parking.yardMap.noMapManageDesc') : t('parking.yardMap.noMapDesc')}
              </Typography>
            </Alert>
          )}

          {yardMap !== null && !imageFailed && image.status === 'loading' && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} aria-label={tc('loading')} />
              <Typography variant="body2" color="text.secondary">
                {t('parking.yardMap.loadingImage')}
              </Typography>
            </Box>
          )}

          {yardMap !== null && (imageFailed || image.status === 'error') && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setFailedRevision(null);
                    image.refetch();
                  }}
                >
                  {t('parking.yardMap.retryImage')}
                </Button>
              }
            >
              {t('parking.yardMap.imageLoadFailed')}
            </Alert>
          )}

          {mapReady && image.url && (
            <Box sx={{ maxWidth: 960 }}>
              <YardMapCanvas
                imageUrl={image.url}
                widthPx={yardMap.widthPx}
                heightPx={yardMap.heightPx}
                imageAlt={t('parking.yardMap.imageAlt')}
                groupLabel={canManage ? t('parking.yardMap.groupLabel') : t('parking.yardMap.groupLabelView')}
                markers={markers}
                mode={canManage ? 'edit' : 'view'}
                instructions={t('parking.yardMap.instructions')}
                disabled={pending}
                onSelect={setSelectedId}
                onMove={(spotId, x, y) => setDraftPosition(spotId, { x: tidy(x), y: tidy(y) })}
                onSurfaceClick={(x, y) => {
                  // Only an explicitly chosen, not yet placed spot is placed by a click.
                  if (selected && selectedPosition === null) {
                    setDraftPosition(selected.spotId, { x: tidy(x), y: tidy(y) });
                  }
                }}
                onImageError={() => setFailedRevision(revisionKey)}
              />
              <Typography variant="caption" color="text.secondary">
                {t('parking.yardMap.imageInfo', { width: yardMap.widthPx, height: yardMap.heightPx })}
              </Typography>
            </Box>
          )}

          {canManage && yardMap !== null && mapReady && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">{t('parking.yardMap.spotsHeading')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('parking.yardMap.spotsHint')}
              </Typography>
              {spots.length === 0 && (
                <Alert severity="info">{t('parking.yardMap.noSpotsToPlace')}</Alert>
              )}
              {status.zones.map((zone) =>
                zone.spots.length === 0 ? null : (
                  <Box key={zone.zoneId} role="group" aria-label={zone.zoneName}>
                    <Typography variant="caption" color="text.secondary" component="h4">
                      {zone.zoneName}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                      {zone.spots.map((statusSpot) => {
                        const spot = spots.find((s) => s.spotId === statusSpot.spotId);
                        if (!spot) return null;
                        const placed = positionOf(spot) !== null;
                        const isSelected = spot.spotId === selectedId;
                        const statusText = placed
                          ? t('parking.yardMap.statusPlaced')
                          : t('parking.yardMap.statusUnplaced');
                        return (
                          <Button
                            key={spot.spotId}
                            size="small"
                            variant={isSelected ? 'contained' : 'outlined'}
                            color={placed ? 'primary' : 'inherit'}
                            aria-pressed={isSelected}
                            aria-label={t('parking.yardMap.spotChoiceAria', {
                              identifier: spot.identifier,
                              zone: spot.zoneName,
                              status: statusText,
                            })}
                            disabled={pending}
                            onClick={() => setSelectedId(spot.spotId)}
                            sx={{
                              minWidth: 72,
                              minHeight: 44,
                              display: 'flex',
                              flexDirection: 'column',
                              textTransform: 'none',
                              lineHeight: 1.2,
                            }}
                          >
                            <Typography component="span" variant="body2" fontWeight={700}>
                              {spot.identifier}
                            </Typography>
                            <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
                              {statusText}
                            </Typography>
                          </Button>
                        );
                      })}
                    </Stack>
                  </Box>
                ),
              )}

              <Typography variant="body2" aria-live="polite">
                {selected
                  ? t('parking.yardMap.selectedSpot', {
                      identifier: selected.identifier,
                      zone: selected.zoneName,
                    })
                  : t('parking.yardMap.noSelectedSpot')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  disabled={pending || !selected}
                  onClick={() => selected && setDraftPosition(selected.spotId, { x: 0.5, y: 0.5 })}
                  sx={{ minHeight: 44 }}
                >
                  {t('parking.yardMap.placeAtCenter')}
                </Button>
                <Button
                  variant="outlined"
                  disabled={pending || !selected || selectedPosition === null}
                  onClick={() => selected && setDraftPosition(selected.spotId, null)}
                  sx={{ minHeight: 44 }}
                >
                  {t('parking.yardMap.clearPosition')}
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  disabled={pending || !dirty}
                  onClick={() => void handleSave()}
                  sx={{ minHeight: 44 }}
                >
                  {save.isPending ? t('parking.yardMap.uploading') : t('parking.yardMap.savePlacements')}
                </Button>
                <Button
                  variant="text"
                  disabled={pending || !dirty}
                  onClick={discardDraft}
                  sx={{ minHeight: 44 }}
                >
                  {t('parking.yardMap.discardChanges')}
                </Button>
                {dirty && (
                  <Typography variant="body2" color="warning.main" role="status">
                    {t('parking.yardMap.unsavedChanges')}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </CardContent>

      <ConfirmDialog
        open={replaceRequest !== null}
        title={t('parking.yardMap.replaceTitle')}
        message={t('parking.yardMap.replaceConfirm')}
        confirmText={t('parking.yardMap.replaceConfirmButton')}
        confirmColor="error"
        onConfirm={handleConfirmReplace}
        onCancel={() => setReplaceRequest(null)}
        loading={pending}
      />
      <ConfirmDialog
        open={deleteRequest !== null}
        title={t('parking.yardMap.deleteTitle')}
        message={t('parking.yardMap.deleteConfirm')}
        confirmText={t('parking.yardMap.deleteConfirmButton')}
        confirmColor="error"
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteRequest(null)}
        loading={pending}
      />
    </Card>
  );
}
